/**
 * Adresvelden met PDOK-lookup: postcode + huisnummer vullen straat en plaats,
 * met een escape naar handmatig. Gedeeld door de order-flow
 * (GegevensControleren) en Mijn Kleda (GegevensKaart, plan 2026-10-28 golf 2),
 * zodat er één invoerregel voor een adres is.
 */
import { useEffect, useMemo, useState } from 'react'
import { lookupAdres } from '../api'

export interface AdresFormState {
  postcode: string
  huisnummer: string
  toevoeging: string
  straatnaam: string
  plaats: string
}

/** Strip HTML uit SF formula-adresvelden */
function cleanAdres(adres?: string): string {
  return adres?.replace(/<br\s*\/?>/gi, ', ').replace(/<[^>]*>/g, '') || ''
}

/** Parse formule-adres ("Straat 10 B, 1234 AB Plaats") terug naar losse velden */
export function parseAdres(adres?: string): AdresFormState {
  const clean = cleanAdres(adres)
  const m = clean.match(/^(.*?),\s*(\d{4}\s?[A-Za-z]{2})\s+(.*)$/)
  const straatDeel = m ? m[1].trim() : clean
  const postcode = m ? m[2].toUpperCase() : ''
  const plaats = m ? m[3].trim() : ''
  const sm = straatDeel.match(/^(.+)\s+(\d+)\s*([a-zA-Z0-9-]*)$/)
  if (sm) {
    return { straatnaam: sm[1].trim(), huisnummer: sm[2], toevoeging: sm[3] || '', postcode, plaats }
  }
  return { straatnaam: straatDeel, huisnummer: '', toevoeging: '', postcode, plaats }
}

/** Format postcode-invoer naar "1234 AB" tijdens het typen */
function formatPostcodeInput(raw: string): string {
  const clean = raw.replace(/\s/g, '').toUpperCase()
  if (clean.length >= 5 && /^\d{4}[A-Z]/.test(clean)) {
    return `${clean.slice(0, 4)} ${clean.slice(4, 6)}`
  }
  return raw.toUpperCase()
}

function norm(s?: string): string {
  return (s || '').trim().toLowerCase()
}

export function useAdresForm(huidigAdres?: string) {
  const initial = useMemo(() => parseAdres(huidigAdres), [huidigAdres])
  const [form, setForm] = useState<AdresFormState>(initial)
  const [isLookingUp, setIsLookingUp] = useState(false)
  const [lookupGevonden, setLookupGevonden] = useState<boolean | null>(null)
  const [handmatig, setHandmatig] = useState(false)

  // PDOK-lookup: postcode + huisnummer → straat + plaats (debounced)
  useEffect(() => {
    const pc = form.postcode.replace(/\s/g, '').toUpperCase()
    const hn = form.huisnummer.trim()
    if (!/^\d{4}[A-Z]{2}$/.test(pc) || !hn) return

    const timer = setTimeout(async () => {
      setIsLookingUp(true)
      const result = await lookupAdres(pc, hn)
      setIsLookingUp(false)
      if (result) {
        setForm(f => ({ ...f, straatnaam: result.straat, plaats: result.plaats }))
        setLookupGevonden(true)
      } else {
        setLookupGevonden(false)
      }
    }, 500)
    return () => clearTimeout(timer)
  }, [form.postcode, form.huisnummer])

  function isGewijzigd(): boolean {
    const pcNow = form.postcode.replace(/\s/g, '').toUpperCase()
    const pcInit = initial.postcode.replace(/\s/g, '').toUpperCase()
    return (
      norm(form.straatnaam) !== norm(initial.straatnaam) ||
      norm(form.huisnummer) !== norm(initial.huisnummer) ||
      norm(form.toevoeging) !== norm(initial.toevoeging) ||
      pcNow !== pcInit ||
      norm(form.plaats) !== norm(initial.plaats)
    )
  }

  /** Foutzin voor de klant, of null als het adres compleet is. */
  function valideer(): string | null {
    const pc = form.postcode.replace(/\s/g, '').toUpperCase()
    if (!/^\d{4}[A-Z]{2}$/.test(pc)) return 'Vul een geldige postcode in (bijvoorbeeld 1234 AB)'
    if (!form.huisnummer.trim() || !form.straatnaam.trim() || !form.plaats.trim()) return 'Vul postcode, huisnummer, straat en plaats in'
    return null
  }

  /** Losse delen voor de server: straat met huisnummer, toevoeging apart, postcode als "1234 AB". */
  function delen(): { straat: string; toevoeging: string; postcode: string; plaats: string } {
    const pc = form.postcode.replace(/\s/g, '').toUpperCase()
    return {
      straat: `${form.straatnaam.trim()} ${form.huisnummer.trim()}`,
      toevoeging: form.toevoeging.trim(),
      postcode: `${pc.slice(0, 4)} ${pc.slice(4)}`,
      plaats: form.plaats.trim(),
    }
  }

  return { form, setForm, isLookingUp, lookupGevonden, handmatig, setHandmatig, isGewijzigd, valideer, delen }
}

export function AdresVelden({ adres, disabled }: { adres: ReturnType<typeof useAdresForm>; disabled?: boolean }) {
  const { form, setForm, isLookingUp, lookupGevonden, handmatig, setHandmatig } = adres
  return (
    <>
      <div className="grid grid-cols-2 gap-sm">
        <div>
          <label className="block text-body-sm text-ink-muted mb-xs">Postcode</label>
          <input
            type="text"
            value={form.postcode}
            onChange={(e) => setForm(f => ({ ...f, postcode: formatPostcodeInput(e.target.value) }))}
            placeholder="1234 AB"
            className="input w-full"
            autoComplete="postal-code"
            disabled={disabled}
          />
        </div>
        <div className="grid grid-cols-3 gap-xs">
          <div className="col-span-2">
            <label className="block text-body-sm text-ink-muted mb-xs">Huisnummer</label>
            <input
              type="text"
              value={form.huisnummer}
              onChange={(e) => setForm(f => ({ ...f, huisnummer: e.target.value }))}
              className="input w-full"
              disabled={disabled}
            />
          </div>
          <div>
            <label className="block text-body-sm text-ink-muted mb-xs">Toev.</label>
            <input
              type="text"
              value={form.toevoeging}
              onChange={(e) => setForm(f => ({ ...f, toevoeging: e.target.value }))}
              placeholder="A"
              className="input w-full"
              disabled={disabled}
            />
          </div>
        </div>
      </div>

      {isLookingUp && (
        <p className="text-body-sm text-ink-muted flex items-center gap-xs">
          <span className="w-3.5 h-3.5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          Adres zoeken...
        </p>
      )}

      {/* Gevonden adres tonen, met escape naar handmatig */}
      {!isLookingUp && lookupGevonden === true && !handmatig && form.straatnaam && (
        <p className="text-body-sm text-ink-soft">
          {form.straatnaam} {form.huisnummer}{form.toevoeging ? ` ${form.toevoeging}` : ''}, {form.plaats}{' '}
          <button
            type="button"
            onClick={() => setHandmatig(true)}
            className="text-[12px] font-medium text-primary hover:text-primary-dark"
          >
            Klopt niet?
          </button>
        </p>
      )}

      {/* Handmatige velden als lookup niets vindt of klant corrigeert */}
      {!isLookingUp && (handmatig || lookupGevonden === false) && (
        <div className="grid grid-cols-2 gap-sm">
          <div>
            <label className="block text-body-sm text-ink-muted mb-xs">Straatnaam</label>
            <input
              type="text"
              value={form.straatnaam}
              onChange={(e) => setForm(f => ({ ...f, straatnaam: e.target.value }))}
              className="input w-full"
              disabled={disabled}
            />
          </div>
          <div>
            <label className="block text-body-sm text-ink-muted mb-xs">Plaats</label>
            <input
              type="text"
              value={form.plaats}
              onChange={(e) => setForm(f => ({ ...f, plaats: e.target.value }))}
              className="input w-full"
              disabled={disabled}
            />
          </div>
        </div>
      )}
    </>
  )
}
